import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

import { BrandBlock, FONT, HelpLine, TRIAL_DAYS } from './shell';
import { Button, CodeInput, Heading, Hint, TextField } from './ui';

type Props = {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  error: string;
  locked: boolean;
  pending: boolean;
  ssoEnabled: boolean;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onGoogle: () => void;
  onSSO: () => void;
  totp: boolean;
  totpCode: string;
  setTotpCode: (v: string) => void;
  onTotp: () => void;
  onBack: () => void;
};

/** Sign-in on training tenants, in the portal's own look. The login page keeps the logic. */
const TrainingLogin = (p: Props) => (
  <div
    className="min-h-screen bg-[#F5F7FB] text-[#1A2130]"
    style={{ fontFamily: FONT }}
  >
    <div className="flex min-h-screen flex-col gap-8 p-5 md:p-8">
      <BrandBlock row />
      <div className="flex flex-1 items-center justify-center">
        <div className="flex w-full max-w-[420px] flex-col rounded-[16px] border border-[#EEF2F7] bg-white p-6 md:p-8">
          {!p.totp ? (
            <form onSubmit={p.onSubmit} className="flex flex-col gap-[18px]">
              <Heading title="Sign in">
                Welcome back to DeepEcho Training.
              </Heading>
              <TextField
                label="Email"
                type="email"
                value={p.email}
                onChange={p.setEmail}
                autoComplete="email"
              />
              <div className="flex flex-col gap-[6px]">
                <TextField
                  label="Password"
                  type="password"
                  value={p.password}
                  onChange={p.setPassword}
                  autoComplete="current-password"
                />
                <Link
                  to="/forgot-password"
                  className="self-end text-[12px] font-semibold text-[#0C51E6]"
                >
                  Forgot password?
                </Link>
              </div>
              {p.error && <Hint tone="error">{p.error}</Hint>}
              <Button type="submit" loading={p.pending} disabled={p.locked}>
                Sign in
              </Button>
              <div className="flex items-center gap-3 text-[12px] text-[#9AA4B2]">
                <span className="h-px flex-1 bg-[#EEF2F7]" />
                or
                <span className="h-px flex-1 bg-[#EEF2F7]" />
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={p.onGoogle}
                disabled={p.pending}
              >
                Continue with Google
              </Button>
              {p.ssoEnabled && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={p.onSSO}
                  disabled={p.pending}
                >
                  Sign in with Workspace SSO
                </Button>
              )}
              <span className="text-center text-[13px] text-[#5B6472]">
                New to DeepEcho Training?{' '}
                <Link to="/signup" className="font-semibold text-[#0C51E6]">
                  Start your {TRIAL_DAYS}-day free trial
                </Link>
              </span>
            </form>
          ) : (
            <div className="flex flex-col gap-[18px]">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF4FF]">
                <ShieldCheck size={22} className="text-[#0C51E6]" />
              </span>
              <Heading title="Two-step verification">
                Enter the 6-digit code from your authenticator app.
              </Heading>
              <CodeInput value={p.totpCode} onChange={p.setTotpCode} large />
              {p.error && <Hint tone="error">{p.error}</Hint>}
              <Button
                type="button"
                onClick={p.onTotp}
                loading={p.pending}
                disabled={p.totpCode.length !== 6}
              >
                Verify
              </Button>
              <button
                type="button"
                onClick={p.onBack}
                className="inline-flex items-center gap-[6px] self-center text-[13px] font-semibold text-[#0C51E6]"
              >
                <ArrowLeft size={14} /> Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
      <span className="text-center">
        <HelpLine />
      </span>
    </div>
  </div>
);

export default TrainingLogin;
